import { ProposalListResponseSchema, type ProposalListItem } from "@fam/shared";

export const apiOrigin = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/$/, "") ?? "";

export function apiUrl(path: string): string {
  return `${apiOrigin}${path}`;
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(path), { ...init, credentials: "include" });
}

export async function getProposals(signal?: AbortSignal): Promise<ProposalListItem[]> {
  const response = await apiFetch("/api/v1/proposals", { signal });
  if (!response.ok) throw new Error(`Proposal request failed with ${response.status}`);
  return ProposalListResponseSchema.parse(await response.json()).proposals;
}

export async function decideProposal(
  id: string,
  decision: "approve" | "reject",
): Promise<{ id: string; state: "approved" | "rejected" }> {
  const response = await apiFetch(`/api/v1/proposals/${encodeURIComponent(id)}/${decision}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
  if (!response.ok) throw new Error(`Proposal decision failed with ${response.status}`);
  return response.json() as Promise<{ id: string; state: "approved" | "rejected" }>;
}

export async function editAndApproveProposal(
  id: string,
  title: string,
): Promise<{ id: string; title: string; state: "edited_approved" }> {
  const response = await apiFetch(`/api/v1/proposals/${encodeURIComponent(id)}/edit-and-approve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ title }),
  });
  if (!response.ok) throw new Error(`Proposal edit failed with ${response.status}`);
  return response.json() as Promise<{ id: string; title: string; state: "edited_approved" }>;
}

export async function captureText(input: {
  text: string;
  visibility: "shared" | "adults" | "private" | "protected";
  retention: "active_operational";
}): Promise<{ id: string; status: "stored" }> {
  const response = await apiFetch("/api/v1/inbox/text", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Capture failed with ${response.status}`);
  return response.json() as Promise<{ id: string; status: "stored" }>;
}
